/*
 *  -----------------------------------------------------------------------
 *    <copyright file="crfs-svt-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import cn from 'clsx';

import AssignCrfDialog from '@components/admin/assign-crf';
import { CRFS_SVT_GRID_COLUMNS } from '@components/admin/crfs-svt-grid/crfs-svt-grid.data';
import UnassignCRFConfirmationDialog from '@components/admin/unassign-crf-confirmation-dialog';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
} from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { CRFsSVTGridProps, StudyCrfFieldsExtended } from './crfs-svt-grid.interface';
import { useBoolean, useLocalState, useTranslation } from 'hooks';
import { GET_STUDY_CRF_ASSESSMENTS, GET_STUDY_CRFS } from 'queries/study-crfs';
import NotificationService from 'utils/notification';
import Loading from 'views/components/core/loading';
import { convertRules } from '@utils/convertor';

import { StudyCrfFields, StudyAssessmentFields } from '@queries/generated/fragments.generated';

import './styles.scss';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';

const CRFsSVTGrid = ({
    currentScheduleId,
    currentScheduleVersion,
    canEdit,
    currentVisit,
    currentItem,
    setCurrentItem,
    switchMenu,
    assessmentAssociation,
}: CRFsSVTGridProps) => {
    const { t } = useTranslation();
    const { state: localState } = useLocalState();
    const study = localState.selectedStudy;
    const studyId = study.id;

    const [visitsCrfs, setVisitsCrfs] = useState<StudyCrfFields[]>([]);
    const [currentCRF, setCurrentCRF] = useState<StudyCrfFields | null>(currentItem ?? null);

    const openAssignCRF = useBoolean(false);
    const openUnassignDialog = useBoolean(false);

    const [loadData, { data, loading }] = useLazyQuery(GET_STUDY_CRFS, { fetchPolicy: 'no-cache' });
    const { data: assessmentsData, loading: assessmentsLoading } = useQuery(GET_STUDY_CRF_ASSESSMENTS, {
        variables: { studyId },
    });

    const associatedView = !!currentVisit;

    useEffect(() => {
        if (studyId) {
            loadData({ variables: { studyId } });
        }
    }, [studyId]);

    const getAssessmentName = (assessments: StudyAssessmentFields[], crfId: string) => {
        const assessmentsNames = assessments
            .filter((assessment) => assessment.crfId === crfId)
            .map((assessment) => assessment.name);
        return assessmentsNames.join(', ');
    };

    const getExtendedCrf = (crf: StudyCrfFields, includeAssessmentTitles: boolean): StudyCrfFieldsExtended => ({
        ...crf,
        _crfRules: convertRules(crf.crfRules),
        ...(includeAssessmentTitles
            ? {
                  _assessmentsTitles: assessmentAssociation
                      ? getAssessmentName(assessmentsData.crfAssessments, crf.id)
                      : '',
              }
            : {}),
    });

    useEffect(() => {
        if (data) {
            let visitsCrfArray: StudyCrfFieldsExtended[] = [];

            if (associatedView && assessmentsData) {
                data.crfs.forEach((crf: StudyCrfFields) => {
                    currentVisit?.eventIds?.forEach((crfId) => {
                        if (crf.id === crfId) {
                            visitsCrfArray.push(getExtendedCrf(crf, true));
                        }
                    });
                });
            } else if (assessmentsData) {
                visitsCrfArray = data.crfs.map((crf: StudyCrfFields) => getExtendedCrf(crf, true));
            } else {
                visitsCrfArray = data.crfs.map((crf: StudyCrfFields) => getExtendedCrf(crf, false));
            }

            const newData = restructuredUpdatedCreatedBy(visitsCrfArray);
            setVisitsCrfs(
                orderBy(newData, [
                    { field: 'active', dir: 'desc' },
                    { field: 'crfName', dir: 'asc' },
                ]),
            );
        }
    }, [data, assessmentsData, currentVisit]);

    const extraMenuItems: ExtraMenuItem[] = associatedView
        ? [
              {
                  position: ExtraMenuItemPosition.expandMenu,
                  customClassName: 'grid-header__item--always-visible',
                  expandMenuItems: [
                      {
                          label: 'admin:assignCRFs',
                          onClick: () => openAssignCRF.setTrue(),
                          icon: ['fal', 'add'],
                          enabled: canEdit,
                      },
                  ],
              },
          ]
        : [];

    const closeAssignDialog = (action?: string, amount?: number) => {
        if (action === 'error') NotificationService.showNotification(t('errorMessage'));
        else {
            if (action === 'success' && amount)
                NotificationService.showNotification(
                    t('admin:successfulAssignedCRFsToVisit', {
                        amount,
                        id: currentVisit?.recordId || currentVisit?.id,
                    }),
                );

            openAssignCRF.setFalse();
        }
    };

    const closeUnassignDialog = (action?: string) => {
        if (action === 'error') {
            NotificationService.showNotification(t('errorMessage'));
        } else {
            if (action === 'unassign' && currentVisit) {
                NotificationService.showNotification(
                    t('admin:crfUnassignedMessage', {
                        crfId: currentCRF?.recordId || currentCRF?.id,
                        visitId: currentVisit.recordId || currentVisit.id,
                    }),
                );
            }

            openUnassignDialog.setFalse();
        }
    };

    const getRowActions = (crf: StudyCrfFields): IActionItemV2[] => [
        {
            label: 'admin:unassignCRF',
            icon: ['fal', 'unlink'],
            onClick: () => {
                setCurrentCRF(crf);
                openUnassignDialog.setTrue();
            },
            enabled: canEdit,
        },
    ];

    const onSelectItem = (crf: StudyCrfFields) => {
        setCurrentCRF(crf);
        if (setCurrentItem) {
            setCurrentItem(crf);
        }
    };

    useEffect(() => {
        if (visitsCrfs && setCurrentItem) {
            onSelectItem(visitsCrfs[0]);
        }
    }, [visitsCrfs]);

    if (!studyId || loading || assessmentsLoading) return <Loading />;

    const exportFileName = `${study?.studyNumber}_SVT_CRFs ${
        currentScheduleVersion ? `${currentScheduleVersion}` : ''
    }`;

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    return (
        <div className={cn('grid-container', { 'tab-container': associatedView })}>
            {!!currentVisit && (
                <>
                    <AssignCrfDialog
                        currentVisit={currentVisit}
                        currentScheduleId={currentScheduleId}
                        studyId={studyId}
                        open={openAssignCRF.value}
                        close={closeAssignDialog}
                    />
                    <UnassignCRFConfirmationDialog
                        studyId={studyId}
                        currentScheduleId={currentScheduleId}
                        currentCRF={currentCRF}
                        visitCRFs={currentVisit?.eventIds || []}
                        currentVisit={currentVisit}
                        open={openUnassignDialog.value}
                        close={closeUnassignDialog}
                    />
                </>
            )}
            <ExpandableGridV2
                className="svt-grid-vertical-scroll"
                title={t(associatedView ? 'common:CRFs' : 'common:allStudyCRFs')}
                customKey="crfs-grid"
                id="svtCrfsGrid"
                key="svtCrfsGrid"
                items={visitsCrfs}
                showFirstColumn={associatedView}
                fixedScroll={!associatedView}
                selectable={{ enabled: !associatedView }}
                defaultSelected={!associatedView ? (currentCRF ?? visitsCrfs[0]) : undefined}
                hideCheckbox
                hideExpand
                scrollable="scrollable"
                groupable
                reorderable
                resizable
                pageable={false}
                setCurrentItem={onSelectItem}
                switchMenu={switchMenu}
                rowActions={getRowActions}
                extraMenuItems={extraMenuItems}
                columns={CRFS_SVT_GRID_COLUMNS(t)}
                exportConfig={!associatedView ? exportConfig : null}
            />
        </div>
    );
};

export default CRFsSVTGrid;
